
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';

import { Room, User, UserRoomStateData, RoomObject, CoordinatesPosition } from '@/utils/models';
import RoomMap from '../room/RoomMap.vue';
import { MAX_AVATAR_WIDTH_AND_HEIGHT } from '@/utils/constants';

@Component({ components: { RoomMap } })
export default class PictureInPicture extends Vue {
  @Prop() room!: Room;
  @Prop() user!: User;
  @Prop() participants!: UserRoomStateData[];
  @Prop() objects!: RoomObject[];
  @Prop() currentSubRoomId!: string;
  @Prop() roomScale!: number;
  @Prop() scale!: number;
  @Prop() userPosition!: CoordinatesPosition;
  @Prop() userParticipantData!: UserRoomStateData | undefined;
  @Prop() connecting!: boolean;

  pipMode: 'full' | '33%' | '50%' | '100%' = '33%';
  /** room background を サイズ縮小して dataurl にしたもの mount 時に作成 */
  private bgImage: HTMLImageElement | null = null;

  get size(): { width: number; height: number } {
    const { width, height } = this.room.roomSize;
    const max = Math.max(width, height);
    const ratio = 640 / max; // 大きいほうが 640 以下になるように
    // const ratio = 1;
    return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
  }

  private isCapture = false;
  private timer: any = 0;
  mounted(): void {
    const video = this.$refs.pipVideo as HTMLVideoElement;
    const canvas = this.$refs.pipCanvas as HTMLCanvasElement;
    const stream = canvas.captureStream(30);
    video.srcObject = stream;
    video.onleavepictureinpicture = (ev) => {
      this.isCapture = false;
      this.pipMode = '33%';
      clearTimeout(this.timer);
      this.$store.commit('setIsCapturePictureInPicture', false);
      this.$store.commit('setPipMode', '33%');
    };
    video.play();
    this.updateBgImageUri();
  }
  beforeDestroy() {
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture();
    }
  }
  private async updateBgImageUri() {
    const img = new Image();
    img.addEventListener(
      'load',
      () => {
        this.bgImage = img;
        this.updateFrame();
      },
      { once: true },
    );
    img.src = this.room.roomMapUrl;
  }
  openPictureInPicture() {
    if (document.pictureInPictureElement) {
      switch (this.pipMode) {
        case '33%':
          this.pipMode = '50%';
          this.$store.commit('setPipMode', '50%');
          break;
        case '50%':
          this.pipMode = '100%';
          this.$store.commit('setPipMode', '100%');
          break;
        case '100%':
          this.pipMode = 'full';
          this.$store.commit('setPipMode', 'full');
          break;
        case 'full':
          this.pipMode = '50%';
          this.$store.commit('setPipMode', '50%');
          break;
      }
    } else {
      const video = this.$refs.pipVideo as HTMLVideoElement;
      video.requestPictureInPicture();
    }
    this.startCapture();
  }
  private startCapture() {
    this.isCapture = true;
    this.updateFrame();
    this.$store.commit('setIsCapturePictureInPicture', true);
  }
  requestUpdateFrame() {
    if (this.isCapture) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.updateFrame();
      }, 100);
    }
  }
  private async updateFrame() {
    clearTimeout(this.timer);
    const start = Date.now();
    const canvas = this.$refs.pipCanvas as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx || !this.bgImage) {
      return;
    }
    const { width, height } = this.size;
    const el = this.$refs.container as HTMLDivElement;
    this.log(`update Frame. Clone start`);
    const svg = el.children[0].cloneNode(true) as SVGElement;
    const fo = svg.children[0] as Element;

    this.log(`update Frame. Clone end.${Date.now() - start}ms`);
    [...document.styleSheets].forEach((styleSheet) => {
      try {
        const cssRules = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
        const style = document.createElement('style');

        style.textContent = cssRules;
        fo.appendChild(style);
      } catch (e) {
        const link = document.createElement('link');

        link.rel = 'stylesheet';
        link.type = styleSheet.type;
        // link.media = styleSheet.media;
        link.href = styleSheet.href as string;
        fo.appendChild(link);
      }
    });
    this.log(`update Frame. Clone style sheets end.${Date.now() - start}ms`, svg);

    const imgList = [...svg.querySelectorAll('img')];
    await Promise.all(
      imgList
        .filter((el) => el.src && !el.src.startsWith('data:'))
        .map(async (el) => {
          const data = await fetch(el.src).then((res) => res.blob());
          const reader = new FileReader();
          const url = await new Promise<string>((resolve) => {
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(data);
          });
          el.src = url;
          await el.decode();
        }),
    );

    const svgStr = new XMLSerializer().serializeToString(svg);
    this.log(`update Frame. Serialize end.${Date.now() - start}ms`);
    const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);

    // svgを画像として読み込む
    const img = new Image(width, height);
    img.src = svgUrl;
    await img.decode();
    this.log(`update Frame. image loaded.${Date.now() - start}ms`);

    requestAnimationFrame(() => {
      this.draw(ctx, img, width, height);
      this.log(`update Frame. updated.${Date.now() - start}ms`);
    });

    if (!this.isCapture) {
      return;
    }
    this.timer = setTimeout(() => {
      this.updateFrame();
    }, 60 * 1000); // 1分に１回は必ず更新する
  }
  private draw(ctx: CanvasRenderingContext2D, img: HTMLImageElement, width: number, height: number) {
    const bg = this.bgImage;
    if (bg) {
      const mode = this.pipMode || '';
      let ratio = 1;
      switch (mode) {
        case '33%':
          ratio++;
        case '50%':
          ratio++;
        case '100%':
          {
            const { x, y } = {
              x: this.userPosition.x + MAX_AVATAR_WIDTH_AND_HEIGHT / 2,
              y: this.userPosition.y + MAX_AVATAR_WIDTH_AND_HEIGHT / 2,
            };
            const { width, height } = this.room.roomSize;
            const cwidth = this.size.width * ratio;
            const cheight = this.size.height * ratio;
            const xOffset = cwidth / 2;
            const yOffset = cheight / 2;
            let sx = Math.min(Math.max(0, x - xOffset), width - cwidth);
            let sy = Math.min(Math.max(0, y - yOffset), height - cheight);
            this.log('update frame. ', sx, sy, cwidth, cheight, 0, 0, cwidth, cheight);
            ctx.drawImage(bg, sx, sy, cwidth, cheight, 0, 0, this.size.width, this.size.height);
            ctx.drawImage(img, sx, sy, cwidth, cheight, 0, 0, this.size.width, this.size.height);
          }
          break;
        case 'full':
        default:
          ctx.drawImage(bg, 0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
      }
    }
  }
  private log(message: string, ...args: any[]) {
    console.log.apply(null, [message, ...args]);
  }
}
